import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import I18n from '@/modules/i18n';
import useFlags from '@/hooks/useFlags';
import { Button } from '@/components/ui/Buttons';
import { TCIDropdown } from '@/components/ui/Dropdown';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import QueryKeys from '@/utils/QueryKeys';
import { useModalManager } from '@/components/common/Modal';
import BulkGradingModal from '@/components/shared/Grading/Navigation/BulkGrading/BulkGradingModal';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import useQuestionScoresData from '../useQuestionScoresData';
import useBulkGradeRows, { postScore } from './useBulkGradeRows';
import useAIGrading from './useAIGrading';
import GradeFasterItem from './GradeFasterItem';
import SuggestScoresItem from './SuggestScoresItem';

// AIDEV-NOTE: The single "Grade faster" entry point on both grading views when AI grading is on. Grade By
// Question's bulk grade keeps its number-score dialog (PUT + reload); Grade By Student's uses credit mode.
const GradeFasterMenu = () => {
  const { aiGradingActive } = useFlags();
  const {
    assignment,
    classroom,
    isQuestionMode,
    isTeacher,
    student,
    submissionForStudent,
  } = useGradingStore();
  const queryClient = useQueryClient();
  const modalManager = useModalManager();
  const studentRows = useBulkGradeRows();
  const { rows: questionRows } = useQuestionScoresData();
  const rows = isQuestionMode ? questionRows : studentRows;
  const { eligibility, startMutation } = useAIGrading();
  const [openedWithPointer, setOpenedWithPointer] = useState(false);

  const showBulkGrade = rows.length > 0;
  const showAIGrading = !!eligibility?.writing_question_count;

  if (!aiGradingActive || !isTeacher || !(showBulkGrade || showAIGrading)) return null;

  const refreshScores = () => {
    queryClient.invalidateQueries(['submission', assignment.id.toString(), student.id.toString()]);
    queryClient.invalidateQueries(['studentAnswer', submissionForStudent(student.id)?.user_id]);
    queryClient.invalidateQueries(['studentAnswer', classroom?.id]);
    queryClient.invalidateQueries([QueryKeys.AIGradingEligibility]);
  };

  return (
    <>
      <TCIDropdown sideOffset={8}>
        <TCIDropdown.Trigger asChild>
          <Button
            outlineStyle
            className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 !tw-px-2 !tw-py-1.5 tw-shadow"
            onKeyDown={() => setOpenedWithPointer(false)}
            onPointerDown={() => setOpenedWithPointer(true)}
          >
            {I18n.t('grade_faster')}
            <Icon icon="CaretClosed" width={10} />
          </Button>
        </TCIDropdown.Trigger>
        <TCIDropdown.Content
          className={cn(
            'tw-box-border tw-w-80 tw-p-px tw-rounded tw-bg-white tw-outline-none',
            'tw-border tw-border-solid tw-border-med-grey tw-shadow-md',
          )}
          width="unset"
        >
          {showAIGrading && (
            <SuggestScoresItem
              eligibility={eligibility}
              openedWithPointer={openedWithPointer}
              starting={startMutation.isLoading}
              onStart={() => startMutation.mutate()}
            />
          )}
          {showBulkGrade && (
            <GradeFasterItem
              description={I18n.t('bulk_grade_menu_description')}
              icon="Layers"
              openedWithPointer={openedWithPointer}
              title={I18n.t('bulk_grade_menu_item')}
              onSelect={modalManager.open}
            />
          )}
        </TCIDropdown.Content>
      </TCIDropdown>

      {modalManager.isOpen && (isQuestionMode ? (
        <BulkGradingModal
          assignment={assignment}
          closeModal={modalManager.close}
          isOpen={modalManager.isOpen}
          scoresData={rows}
        />
      ) : (
        <BulkGradingModal
          closeModal={modalManager.close}
          isOpen={modalManager.isOpen}
          saveScore={postScore}
          scoreMode="credit"
          scoresData={rows}
          onComplete={refreshScores}
        />
      ))}
    </>
  );
};

export default GradeFasterMenu;
